import { useTranslation } from "react-i18next";
import { formatStatus } from "@shared/schema";

/**
 * Domains that have a `status.<domain>.<enumValue>` translation namespace.
 * Keep in sync with the enums in shared/schema.ts.
 */
export type StatusDomain = "order" | "quote" | "job" | "userRole";

/**
 * Returns a function that converts a raw enum status value (e.g. "in_progress")
 * into its localized human-readable label.
 *
 * Falls back to formatStatus() — mechanical title-case from the DB value — when
 * the translation key is missing. That way a newly added enum value still
 * renders something sensible until the translator catches up.
 *
 * Usage:
 *   const formatOrderStatus = useStatusLabel("order");
 *   <StatusBadge status={order.status} statusColors={STATUS_COLORS} formatStatus={formatOrderStatus} />
 */
export function useStatusLabel(domain: StatusDomain): (status: string) => string {
  const { t } = useTranslation();
  return (status: string) => {
    if (!status) return formatStatus(status);
    return t(`status.${domain}.${status}`, {
      defaultValue: formatStatus(status),
    });
  };
}
